import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';

import { useGetMemberId } from 'hooks/useGetMemberId';
import { useQueryBillingProperties } from 'pages/check-credit-card-info/queries';
import { useCheckNeedToAddCreditCard } from 'pages/check-credit-card-info/hooks';

import { FormButton } from 'common/components';
import { CaretRightOutlined } from '@ant-design/icons';
import { forwardTo } from 'utils/common/route';
import * as ribbonActions from '@redux/ribbon/actions';
import { sleep } from 'utils/delay';
import {
  selectWarningMissingCard,
  changedStatusShowMissingCard,
} from './creditCardSlice';
/**
 ** Check if need to show alert require user to add credit card
 */
const notificationKey = 'no-credit-card-member-warning';

export const useCheckInvoicedPayment = () => {
  const { memberId } = useGetMemberId();

  const { billingProperties, isLoading, ...rest } = useQueryBillingProperties({
    memberId,
    staleTime: 0,
  });

  return {
    isInvoicedTerm:
      billingProperties?.billingTerms?.toLowerCase() === 'invoiced' &&
      !isLoading,
    isLoading,
    ...rest,
  };
};

export const useCheckShowCreditCardAlertWhenOpenApp = () => {
  const { isNeedAddCreditCard } = useCheckNeedToAddCreditCard();

  const { memberId } = useGetMemberId();

  const dispatch = useDispatch();

  const { showWarningWhenNoCreditCard, closeCreditCardMissingCard } =
    useShowWarningWhenNoCreditCard();

  const statusShowMissingCard = useSelector(selectWarningMissingCard());

  const { isInvoicedTerm, isLoading } = useCheckInvoicedPayment();

  const { billingProperties } = useQueryBillingProperties({
    memberId,
    staleTime: 0,
  });

  const billingTerms = billingProperties?.billingTerms?.toLowerCase();

  useEffect(() => {
    if (!isLoading) {
      if (isNeedAddCreditCard && !isInvoicedTerm && billingTerms) {
        dispatch(
          changedStatusShowMissingCard({
            status: 'show',
          })
        );
      } else {
        dispatch(
          changedStatusShowMissingCard({
            status: 'hidden',
          })
        );
      }
    }
  }, [isNeedAddCreditCard, isInvoicedTerm, billingTerms, isLoading, dispatch]);

  useEffect(() => {
    if (statusShowMissingCard === 'show') {
      showWarningWhenNoCreditCard();
    }

    if (
      statusShowMissingCard === 'idle' ||
      statusShowMissingCard === 'hidden'
    ) {
      closeCreditCardMissingCard();
    }

    return () => {
      closeCreditCardMissingCard();
    };
  }, [
    showWarningWhenNoCreditCard,
    statusShowMissingCard,
    closeCreditCardMissingCard,
  ]);
};

export const useShowWarningWhenNoCreditCard = () => {
  const dispatch = useDispatch();

  const closeCreditCardMissingCard = useCallback(() => {
    notification.close(notificationKey);
  }, []);

  const showWarningWhenNoCreditCard = useCallback(async () => {
    const goToMyCompanyBillingTab = () => {
      forwardTo('/company');
      notification.close(notificationKey);
      setTimeout(() => {
        dispatch(ribbonActions.changeMemberLayout('billing'));
      }, [1500]);
    };

    const des = (
      <div>
        <p>
          To continue accessing all our service features, please add a credit
          card to your account.
        </p>
        <FormButton
          text={
            <span>
              Go to Billing view <CaretRightOutlined />
            </span>
          }
          style={{ float: 'right' }}
          icon={null}
          onClick={goToMyCompanyBillingTab}
        />
      </div>
    );

    closeCreditCardMissingCard();

    await sleep(500);

    notification.warn({
      key: notificationKey,
      message: 'Credit card info is missing!',
      description: des,
      duration: 60,
      placement: 'bottomRight',
    });
  }, [dispatch, closeCreditCardMissingCard]);

  return { showWarningWhenNoCreditCard, closeCreditCardMissingCard };
};
